<template>
    <div>
        <b-modal :visible="visible" @hide="handleHide" @hidden="handleClose" size="xl"
            title="Documentos del Diagnóstico" no-close-on-backdrop scrollable>
            <b-overlay :show="loading" rounded="sm">
                <div v-if="diagnostic && !loading">
                    <!-- Información del diagnóstico -->
                    <div class="diagnostic-info mb-4">
                        <h5 class="mb-3">
                            <i class="fa fa-clipboard"></i> Información del Diagnóstico
                        </h5>
                        <b-row>
                            <b-col md="6">
                                <div class="info-item">
                                    <strong>Lista de Chequeo:</strong>
                                    <span>{{ getCheckListName }}</span>
                                </div>
                            </b-col>
                            <b-col md="6">
                                <div class="info-item">
                                    <strong>Ubicación:</strong>
                                    <span>{{ getLocationDescription }}</span>
                                </div>
                            </b-col>
                            <b-col md="6">
                                <div class="info-item">
                                    <strong>Champion / Responsable principal:</strong>
                                    <span>{{ getResponsibleName }}</span>
                                </div>
                            </b-col>
                            <b-col md="6">
                                <div class="info-item">
                                    <strong>Fecha:</strong>
                                    <span>{{ diagnostic.diagnostic_date | formatDate }}</span>
                                </div>
                            </b-col>
                        </b-row>
                    </div>

                    <hr>

                    <!-- Lista de documentos -->
                    <div class="documents-section">
                        <h5 class="mb-3">
                            <i class="fa fa-file-text"></i> Documentos Requeridos
                            <b-badge variant="info" class="ml-2">
                                {{ documentsWithFiles.length }} / {{ sortedDocuments.length }} cargados
                            </b-badge>
                        </h5>

                        <div v-if="sortedDocuments.length === 0" class="text-center text-muted py-4">
                            <i class="fa fa-info-circle fa-2x mb-2"></i>
                            <p>No hay documentos asociados a este diagnóstico</p>
                        </div>

                        <div v-else class="documents-list">
                            <div v-for="document in sortedDocuments" :key="document.id" class="document-card"
                                :class="{ 'has-file': hasFile(document), 'no-file': !hasFile(document) }">
                                <div class="document-header">
                                    <div class="document-info">
                                        <div>
                                            <div class="document-name">{{ getDocumentName(document) }}</div>
                                            <small v-if="document.document_list && document.document_list.description" class="text-muted">
                                                {{ document.document_list.description }}
                                            </small>
                                        </div>
                                    </div>
                                    <div class="document-actions">
                                        <b-button v-if="hasFile(document) && isPDF(document.path)" size="sm"
                                            variant="info" @click="viewDocument(document)"
                                            :disabled="viewing[document.id]" class="mr-2">
                                            <i class="fa fa-eye"></i> Ver
                                        </b-button>
                                        <b-button v-if="hasFile(document)" size="sm" variant="success"
                                            @click="downloadDocument(document)" :disabled="downloading[document.id]"
                                            class="mr-2">
                                            <i class="fa fa-download"></i> Descargar
                                        </b-button>
                                        <b-button size="sm" variant="primary" @click="selectFile(document)"
                                            :disabled="uploading[document.id]">
                                            <i class="fa fa-upload"></i>
                                            {{ hasFile(document) ? 'Reemplazar' : 'Subir' }}
                                        </b-button>
                                    </div>
                                </div>

                                <!-- Archivo cargado -->
                                <div v-if="hasFile(document)" class="document-file">
                                    <div class="file-preview">
                                        <i :class="getFileIcon(document.path)" class="fa-2x"></i>
                                        <div class="file-info ml-3">
                                            <div class="file-name">{{ getFileName(document) }}</div>
                                            <small v-if="document.updated_at || document.created_at" class="text-muted">
                                                Cargado el {{ (document.updated_at || document.created_at) | formatDate
                                                }}
                                            </small>
                                        </div>
                                    </div>
                                </div>

                                <!-- Estado de carga -->
                                <div v-if="uploading[document.id]" class="upload-progress">
                                    <b-progress :value="100" animated class="mb-2"></b-progress>
                                    <small class="text-muted">
                                        <i class="fa fa-spinner fa-spin"></i> Subiendo archivo...
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </b-overlay>

            <template #modal-footer>
                <b-button variant="secondary" @click="handleClose">
                    Cerrar
                </b-button>
            </template>

            <!-- Input file oculto -->
            <input ref="fileInput" type="file" style="display: none" accept=".pdf,.doc,.docx,.xls,.xlsx"
                @change="handleFileSelected" />
        </b-modal>

        <!-- Modal para visualizar PDF -->
        <b-modal ref="pdfViewerModal" size="xl" :title="pdfViewerTitle" hide-footer @hidden="closePdfViewer">
            <PdfViewer v-if="pdfViewerData.base64" :base64Content="pdfViewerData.base64"
                :mimeType="pdfViewerData.mimeType" :fileName="pdfViewerData.fileName" />
        </b-modal>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import moment from 'moment';
import PdfViewer from '@/components/PdfViewer.vue';

export default {
    name: 'DiagnosticDocumentsModal',
    components: {
        PdfViewer
    },
    props: {
        visible: {
            type: Boolean,
            default: false
        },
        diagnosticId: {
            type: Number,
            default: null
        },
        companyId: {
            type: Number,
            required: false,
            default: null
        }
    },
    data() {
        return {
            loading: false,
            diagnostic: null,
            uploading: {},
            downloading: {},
            viewing: {},
            selectedDocument: null,
            pdfViewerData: {
                base64: null,
                mimeType: null,
                fileName: null
            },
            pdfViewerTitle: ''
        };
    },
    computed: {
        documents() {
            return this.diagnostic && this.diagnostic.documents ? this.diagnostic.documents : [];
        },
        sortedDocuments() {
            // Ordenar documentos alfabéticamente por nombre
            return [...this.documents].sort((a, b) => {
                const getName = (doc) => {
                    if (doc.document_list && doc.document_list.name) return doc.document_list.name;
                    if (doc.custom_name) return doc.custom_name;
                    return doc.original_name || '';
                };
                const nameA = getName(a).toLowerCase();
                const nameB = getName(b).toLowerCase();
                return nameA.localeCompare(nameB);
            });
        },
        documentsWithFiles() {
            return this.documents.filter(doc => doc.path);
        },
        getCheckListName() {
            if (!this.diagnostic) return 'N/A';
            if (!this.diagnostic.check_list_company) return 'N/A';
            if (!this.diagnostic.check_list_company.check_list) return 'N/A';
            return this.diagnostic.check_list_company.check_list.name || 'N/A';
        },
        getLocationDescription() {
            if (!this.diagnostic) return 'N/A';
            if (!this.diagnostic.location) return 'N/A';
            return this.diagnostic.location.description || 'N/A';
        },
        getResponsibleName() {
            if (!this.diagnostic) return 'N/A';
            if (!this.diagnostic.responsible) return 'N/A';
            const names = this.diagnostic.responsible.names || '';
            const surnames = this.diagnostic.responsible.surnames || '';
            return `${names} ${surnames}`.trim() || 'N/A';
        }
    },
    watch: {
        visible(newVal) {
            if (newVal && this.diagnosticId) {
                this.loadDiagnostic();
            }
        }
    },
    filters: {
        formatDate(date) {
            return date ? moment(date).format('DD/MM/YYYY') : '-';
        }
    },
    methods: {
        ...mapActions('skudo/diagnostics', ['uploadDiagnosticDocument', 'getDiagnosticById', 'downloadDiagnosticDocument']),

        async loadDiagnostic() {
            if (!this.diagnosticId || !this.companyId) return;

            this.loading = true;
            try {
                const response = await this.getDiagnosticById({
                    id: this.diagnosticId,
                    company_id: this.companyId
                });
                this.diagnostic = response.data.data || response.data;
            } catch (error) {
                this.$alertError('Error al cargar el diagnóstico');
            } finally {
                this.loading = false;
            }
        },

        hasFile(document) {
            return document.path && document.path !== null;
        },

        getDocumentName(doc) {
            if (doc.document_list && doc.document_list.name) return doc.document_list.name;
            if (doc.custom_name) return doc.custom_name;
            return doc.original_name || 'Documento sin nombre';
        },

        getFileName(object) {
            if (!object.original_name) return 'Sin archivo';
            return object.original_name;
        },

        getFileIcon(path) {
            if (!path) return 'fa fa-file-o text-secondary';
            const fileName = path.toLowerCase();

            if (fileName.endsWith('.pdf')) {
                return 'fa fa-file-pdf-o text-danger';
            }
            if (fileName.endsWith('.doc') || fileName.endsWith('.docx')) {
                return 'fa fa-file-word-o text-primary';
            }
            if (fileName.endsWith('.xls') || fileName.endsWith('.xlsx')) {
                return 'fa fa-file-excel-o text-success';
            }
            return 'fa fa-file-o text-secondary';
        },

        isPDF(path) {
            if (!path) return false;
            const fileName = path.toLowerCase();
            return fileName.endsWith('.pdf');
        },

        async selectFile(document) {
            // Si ya tiene archivo, mostrar confirmación
            if (this.hasFile(document)) {
                try {
                    await this.$confirm(
                        '¿Está seguro de reemplazar este documento? Esta acción eliminará el archivo anterior y no se puede deshacer.',
                        'Confirmar reemplazo',
                        {
                            confirmButtonText: 'Sí, reemplazar',
                            cancelButtonText: 'Cancelar',
                            type: 'warning'
                        }
                    );
                } catch {
                    return; // Usuario canceló
                }
            }

            this.selectedDocument = document;
            this.$refs.fileInput.click();
        },

        async handleFileSelected(event) {
            const file = event.target.files[0];
            if (!file || !this.selectedDocument) return;

            // Validar tamaño (10MB)
            if (file.size > 10 * 1024 * 1024) {
                this.$alertError('El archivo no debe superar los 10MB');
                return;
            }

            // Validar tipo
            const validTypes = [
                'application/pdf',
                'application/msword',
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            ];

            if (!validTypes.includes(file.type)) {
                this.$alertError('Solo se permiten archivos PDF, Word o Excel');
                return;
            }

            await this.uploadFile(file);

            // Limpiar input
            event.target.value = '';
        },

        async uploadFile(file) {
            const documentId = this.selectedDocument.id;
            this.$set(this.uploading, documentId, true);

            try {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('company_id', this.companyId);
                formData.append('diagnostic_id', this.diagnostic.id);
                formData.append('document_list_id', this.selectedDocument.document_list_id);

                const response = await this.uploadDiagnosticDocument(formData);

                if (response.status || response.message === 'Documento subido exitosamente') {
                    this.$alertSuccess('Documento subido exitosamente');
                    // Recargar el diagnóstico para actualizar la lista de documentos
                    await this.loadDiagnostic();
                    // Usar nextTick para asegurar que el DOM se actualice antes de emitir
                    this.$nextTick(() => {
                        this.$emit('document-uploaded');
                    });
                } else {
                    this.$alertError(response.message || 'Error al subir el documento');
                }
            } catch (error) {
                this.$alertError('Error al subir el documento');
            } finally {
                this.$set(this.uploading, documentId, false);
            }
        },

        async downloadDocument(doc) {
            this.$set(this.downloading, doc.id, true);

            try {
                const response = await this.downloadDiagnosticDocument({
                    documentId: doc.id,
                    companyId: this.companyId
                });

                if (response.data && response.data.base64) {
                    // Convertir base64 a blob
                    const byteCharacters = atob(response.data.base64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: response.data.mime_type });

                    // Crear URL temporal y descargar
                    const url = window.URL.createObjectURL(blob);
                    const link = window.document.createElement('a');
                    link.href = url;
                    link.download = doc.custom_name || doc.original_name || response.data.file_name || 'documento';
                    window.document.body.appendChild(link);
                    link.click();
                    window.document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    this.$alertSuccess('Archivo descargado exitosamente');
                } else {
                    this.$alertError('Error al descargar el archivo');
                }
            } catch (error) {
                this.$alertError('Error al descargar el documento');
            } finally {
                this.$set(this.downloading, doc.id, false);
            }
        },

        handleHide(bvModalEvent) {
            // Mover el foco al body ANTES de que el modal se oculte
            // Esto evita el warning de aria-hidden
            const activeElement = document.activeElement;
            if (activeElement && this.$el && this.$el.contains(activeElement)) {
                // Si el foco está dentro del modal, moverlo al body
                document.body.focus();
                // Alternativamente, mover al botón que lo abrió
                this.$nextTick(() => {
                    if (document.activeElement === document.body) {
                        document.body.blur();
                    }
                });
            }
        },

        handleClose() {
            // Emitir evento después de que el modal se ha cerrado completamente
            this.$emit('close');
        },

        async viewDocument(doc) {
            this.$set(this.viewing, doc.id, true);

            try {
                const response = await this.downloadDiagnosticDocument({
                    documentId: doc.id,
                    companyId: this.companyId
                });

                if (response.data && response.data.base64) {
                    // Configurar datos del visor
                    this.pdfViewerData = {
                        base64: response.data.base64,
                        mimeType: response.data.mime_type,
                        fileName: response.data.file_name
                    };

                    this.pdfViewerTitle = doc.document_list.name;

                    // Abrir modal del visor
                    this.$refs.pdfViewerModal.show();
                } else {
                    this.$alertError('No se pudo obtener el documento');
                }
            } catch (error) {
                this.$alertError('Error al visualizar el documento');
            } finally {
                this.$set(this.viewing, doc.id, false);
            }
        },

        closePdfViewer() {
            // Limpiar datos al cerrar el visor
            this.pdfViewerData = {
                base64: null,
                mimeType: null,
                fileName: null
            };
            this.pdfViewerTitle = '';
        }
    }
};
</script>

<style scoped>
.diagnostic-info {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 6px;
    border: 1px solid #dee2e6;
}

.info-item {
    margin-bottom: 10px;
}

.info-item strong {
    display: block;
    font-size: 0.85rem;
    color: #6c757d;
    margin-bottom: 4px;
}

.info-item span {
    font-size: 0.95rem;
    color: #495057;
}

.documents-section {
    margin-top: 20px;
}

.documents-list {
    display: flex;
    flex-direction: column;
    gap: 15px;
}

.document-card {
    background: #ffffff;
    border: 2px solid #e0e0e0;
    border-radius: 8px;
    padding: 15px;
    transition: all 0.3s ease;
}

.document-card.has-file {
    border-color: #28a745;
    background: #f8fff9;
}

.document-card.no-file {
    border-color: #f8b4b4;
    background: #fffbfb;
}

.document-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.document-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

.document-info {
    display: flex;
    align-items: flex-start;
    flex: 1;
}

.document-name {
    font-weight: 600;
    font-size: 0.95rem;
    color: #495057;
    line-height: 1.4;
}

.document-file {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #dee2e6;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.file-preview {
    display: flex;
    align-items: center;
    flex: 1;
}

.file-info {
    flex: 1;
}

.file-name {
    font-size: 0.9rem;
    color: #495057;
    font-weight: 500;
}

.file-actions {
    display: flex;
    gap: 8px;
}

.upload-progress {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #dee2e6;
}

.pdf-viewer-container {
    min-height: 400px;
}

.pdf-canvas {
    width: 100%;
    border: 1px solid #dee2e6;
    border-radius: 4px;
}

.pdf-controls {
    position: sticky;
    top: 0;
    background: white;
    z-index: 10;
    padding: 10px 0;
}

@media (max-width: 768px) {
    .document-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }

    .document-actions {
        width: 100%;
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
    }

    .document-actions .btn {
        flex: 1;
        min-width: calc(33.33% - 4px);
        font-size: 0.8rem;
        padding: 0.4rem 0.3rem;
    }

    .document-actions .btn i {
        font-size: 0.85rem;
    }

    .document-file {
        flex-direction: column;
        gap: 10px;
    }

    .file-preview {
        flex-direction: column;
        text-align: center;
    }

    .file-preview i {
        margin-bottom: 8px;
    }

    .file-info {
        margin-left: 0 !important;
    }
}
</style>
